import { create } from 'zustand';
import { updatePreferences } from '../api/client';
import { i18n, LOCALES, type Locale } from '../i18n';

/**
 * UI preferences: theme (light/dark), language, the navigation rail mode and the date format, persisted per user on
 * the server (users.theme / users.locale / users.nav_pinned / users.preferences.date_format). localStorage is only a
 * pre-paint cache; the server value wins after sign-in.
 */
export type Theme = 'light' | 'dark';
/** Date/time layout: Intl for the UI language ('auto') or a fixed numeric layout (ui/format.ts formatDateTime). */
export const DATE_FORMATS = ['auto', 'dd-mm-yyyy', 'yyyy-mm-dd'] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

const read = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
};

export function initialTheme(): Theme {
  const t = document.documentElement.dataset.theme;
  return t === 'dark' ? 'dark' : 'light';
}
export function initialNavPinned(): boolean {
  return read('udm.navPinned', ['1', '0'] as const, '1') === '1';
}
export function initialDateFormat(): DateFormat {
  return read('udm.dateFormat', DATE_FORMATS, 'auto');
}
export function initialLocale(): Locale {
  const browser = navigator.language?.slice(0, 2) as Locale;
  return read('udm.locale', LOCALES, (LOCALES as readonly string[]).includes(browser) ? browser : 'en');
}

interface PrefsState {
  theme: Theme;
  locale: Locale;
  /** rail pinned expanded (labels, content resized) vs collapsed (expands over the content on hover) */
  navPinned: boolean;
  dateFormat: DateFormat;
  /** true while a user is signed in: changes are saved to the profile */
  signedIn: boolean;
  setTheme(t: Theme): void;
  setLocale(l: Locale): void;
  setNavPinned(v: boolean): void;
  setDateFormat(f: DateFormat): void;
  applyFromServer(theme: Theme, locale: Locale, navPinned: boolean, dateFormat: DateFormat): void;
  setSignedIn(v: boolean): void;
}

const applyTheme = (theme: Theme) => {
  document.documentElement.dataset.theme = theme;
  write('udm.theme', theme);
};
const applyLocale = (locale: Locale) => {
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
  write('udm.locale', locale);
};

export const usePrefs = create<PrefsState>((set, get) => ({
  theme: initialTheme(),
  locale: initialLocale(),
  navPinned: initialNavPinned(),
  dateFormat: initialDateFormat(),
  signedIn: false,
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
    if (get().signedIn) void updatePreferences({ theme }).catch(() => undefined);
  },
  setLocale: (locale) => {
    applyLocale(locale);
    set({ locale });
    if (get().signedIn) void updatePreferences({ locale }).catch(() => undefined);
  },
  setNavPinned: (navPinned) => {
    write('udm.navPinned', navPinned ? '1' : '0');
    set({ navPinned });
    if (get().signedIn) void updatePreferences({ nav_pinned: navPinned }).catch(() => undefined);
  },
  setDateFormat: (dateFormat) => {
    write('udm.dateFormat', dateFormat);
    set({ dateFormat });
    if (get().signedIn) void updatePreferences({ date_format: dateFormat }).catch(() => undefined);
  },
  applyFromServer: (theme, locale, navPinned, dateFormat) => {
    applyTheme(theme);
    applyLocale(locale);
    write('udm.navPinned', navPinned ? '1' : '0');
    write('udm.dateFormat', dateFormat);
    set({ theme, locale, navPinned, dateFormat, signedIn: true });
  },
  setSignedIn: (signedIn) => set({ signedIn }),
}));
